import useAlert from "@/composables/useAlert";
import useReduceImageSize, {
    ReduceFileSizeSettings,
} from "@/composables/useReduceImageSize";
import { useStore } from '@/composables/useTypedStore';
import { APhoto } from "@/models/photo/a-photo.model";
import CachedPhoto from "@/models/photo/cached-photo.model";
import { getPhotoUrl, isStrapiPhoto } from "@/utilities/get-media-url";
import { Monitoring } from "@/utilities/monitoring";
import { convertBlobToBase64, createCachedPhotoFromB64, createCachedPhotoFromBlob} from '@/utilities/photo-storage';
import { Media, MediaSaveOptions } from '@capacitor-community/media';
import {
    Camera,
    CameraResultType,
    CameraSource,
    Photo
} from "@capacitor/camera";
import { Directory } from "@capacitor/filesystem";
import { FilePicker } from "@capawesome/capacitor-file-picker";
import { isPlatform } from "@ionic/vue";
import { computed } from "vue";
import {
    addHeading,
    addMetadataToJpeg,
    createExifMetaData
} from './Utilities/jpegMetadata.js';

export interface SelectPhotoResponse {
  success: boolean,
  data: CachedPhoto[] | undefined,
  messageKey?: "portraitOnly"
}

export function usePhotoRecording() {
  const { reduceFileSize, blobToFile, getFileFromWebpath } =
    useReduceImageSize();
    const store = useStore();

  const getCompressedFiles = async (
    path: string,
    settings: ReduceFileSizeSettings,
    format = "jpeg"
  ): Promise<SelectPhotoResponse> => {
    try {
      // Monitoring.consoleOnly("CAMERA PHOTO", cameraPhoto, file);

      const file = await getFileFromWebpath(path);

      return new Promise<SelectPhotoResponse>((resolve, reject) => {
        try {
          Monitoring.consoleOnly("Reducing file size... ");
          reduceFileSize(
            file,
            400 * 1024,
            2000,
            Infinity,
            1,
            settings,
            async (blob: Blob | false) => {
              if (!blob) {
                resolve({ success: false, data: undefined });
                return;
              }

              const exifMetaData = await createExifMetaData(
                store.state.currentHzba.currentBa
              );

              try {
                addHeading(store.state.app.heading, exifMetaData);
              } catch (error: any) {
                // TODO: will always fail on desktop browser, only log error if filtered by mobile devices
                Monitoring.consoleOnly("Error while adding heading to exif metadata", error);
              }

              const newImage = await addMetadataToJpeg(
                await convertBlobToBase64(blob),
                exifMetaData
              );

              const newPhoto = await createCachedPhotoFromB64(newImage, blob.type);

              resolve({
                success: true,
                data: newPhoto
              });
            }
          );
        } catch (err: any) {
          Monitoring.chainError("Error while taking a photo and reducing file size", err);
        }
      });
    } catch (err: any) {
      Monitoring.chainError("Error while taking a photo", err);
      return new Promise<SelectPhotoResponse>((resolve, reject) => {
        resolve({
          success: false,
          data: undefined,
        });
      });
    }
  };

  const getAlbumId = async () => {
    try {
      const findAlbum = async (name: string) => {
        const albumRes = await Media.getAlbums();
        return albumRes.albums.find((item) => {
          Monitoring.consoleOnly("get Album ID: ", item.name);
          return item.name == name;
        });
      };

      let album = await findAlbum("Acon 360°");
      if (!album) {
        await Media.createAlbum({ name: "Acon 360°" });

        album = await findAlbum("Acon 360°");
      }

      return album && (isPlatform("ios") ? album.identifier : album.name);
    } catch (err) {
      Monitoring.consoleOnly("Could not find/create album", err);
    }
  };

  const takePhoto = async (
    settings: ReduceFileSizeSettings
  ): Promise<SelectPhotoResponse> => {
    try {
      const cameraPhoto: Photo = await Camera.getPhoto({
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
        quality: 100
      });
      store.commit("app/setLoadingSpinner", true);
      const compressedRes = await getCompressedFiles(
        cameraPhoto.webPath!,
        settings,
        cameraPhoto.format
      );
      const image =
        compressedRes.success &&
        compressedRes.data &&
        compressedRes.data.length > 0 &&
        compressedRes.data[0];
    //   Monitoring.consoleOnly("Image vs CompressedImage", image, compressedRes);

      if (image && image.path) {
        const opt: MediaSaveOptions = {
          path: image.path,
          albumIdentifier: await getAlbumId(),
        };
        Media.savePhoto(opt)
          .then(() => Monitoring.consoleOnly("photo saved.")) // on android it returns {filePath: 'uri to media'}
          .catch((err: any) => {
            if (!err.message.includes("Not implemented on web")) {
              Monitoring.error("Error while saving photo", err);
            }
          });
      } else {
        Monitoring.error("Image is empty, could not write to system storage.");
        return {
          success: false,
          data: undefined,
          messageKey: "portraitOnly",
        };
      }
      return compressedRes;
    } catch (err: any) {
      Monitoring.error("Error while taking a photo!", err);
      return {
        success: false,
        data: undefined,
      };
    } finally {
      store.dispatch("app/stopRecordingMotion");
      store.commit("app/setLoadingSpinner", false);
    }
  };

  const choosePhotoFromGallery = async (
    limit?: number,
    settings?: ReduceFileSizeSettings
  ): Promise<SelectPhotoResponse> => {
    let reportError = false;

    try {
      const params = limit && limit > 0 ? { limit: limit } : {};
      const res = await Camera.pickImages(params);
      store.commit("app/setLoadingSpinner", true);

      const images =
        limit && limit > 0 ? res.photos.slice(0, limit) : res.photos;
      const compressedFiles: CachedPhoto[] = [];

      for (let i = 0; i < images.length; i++) {
        const compressedImage = await getCompressedFiles(
          images[i].webPath!,
          settings || {},
          images[i].format
        );
        if (compressedImage.success) {
          compressedImage.data && compressedFiles.push(...compressedImage.data);
        } else {
          // Monitoring.error('Error when compressing files', compressedImage);
          // return { success: false, data: undefined }
          reportError = true;
        }
      }

    //   Monitoring.consoleOnly("Choose images....", reportError);
      return {
        data: compressedFiles,
        success: true,
        messageKey: reportError ? "portraitOnly" : undefined,
      };
    } catch (err: any) {
      Monitoring.chainError("Error while taking a photo!", err);
      return {
        success: false,
        data: undefined,
      };
    } finally {
        store.commit("app/setLoadingSpinner", false);
    }
  };

  const pickFiles = async () => {
    const result = await FilePicker.pickFiles({
      types: ["image/png"],
      multiple: true,
    });
  };

  const appendFileToFormData = async ( isPdfUploadType = false ): Promise<SelectPhotoResponse> => {
    const pickFilesConfig: any = { multiple: true, readData: true };
    if ( isPdfUploadType ) {
      pickFilesConfig["types"] = ["application/pdf"];
    }
    let result = null;
    try {
      result = await FilePicker.pickFiles(pickFilesConfig);
    } catch (e) {
      // dialog canceled
      return {
        success: false,
        data: undefined
      };
    }

    const file = result.files[0];
    // const file = await fetch(result.files[0].path)
    // Monitoring.consoleOnly('result from FilePicker: ', result);
    // Monitoring.consoleOnly('result.files[0] from FilePicker: ', result.files[0]);

    // const savedFile = await Filesystem.writeFile({
    //   path: file.name,
    //   data: file.data as string,
    //   directory: Directory.Data
    // });

    // Monitoring.consoleOnly('savedFile:', savedFile);
    const isMobile = computed(() => {
      return (isPlatform("ios") || isPlatform("ipad") || isPlatform("android"));
    });

    const uploadType = file.name.split('.').pop();
    if (!file.blob && file.data && isMobile.value) {

      const randomName = Math.random().toString(36).substr(2, 12) + `.${uploadType}`;

      const newCachedPhoto =  await createCachedPhotoFromB64(file.data, uploadType, randomName, Directory.Documents, file.name);

      const retVal = {
        success: true,
        data: newCachedPhoto,
      };
      return retVal;
    }

    if (!file.blob) {
      return { success: false, data: undefined };
    }

    const cachedPhoto = await createCachedPhotoFromBlob(file.blob, file.name, uploadType);

    const retVal = {
      success: true,
      data: cachedPhoto
    };

    // Monitoring.consoleOnly("Save pdf blob", blobUrl);
    // Monitoring.consoleOnly("Save pdf blob", file.blob);
    // Monitoring.consoleOnly("Save pdf", savedFile)
    // Monitoring.consoleOnly("Save pdf", retVal)
    // }

    return retVal;

    //
    // Monitoring.consoleOnly("append file to form data", formData, file);
    //
    // // @ts-ignore
    // const rawFile = new File(file.blob, file.name, {
    //     type: file.mimeType,
    // });
    // formData.append('file', rawFile, file.name);
  };

  const openPhotoDialog = (
    t: any,
    limit?: number,
    showOptions?: {
      hideFile?: boolean;
      hidePhoto?: boolean;
      hideGallery?: boolean;
      allowPortraitOnly?: boolean;
      isPdfUploadType?: boolean;
    },
  ): Promise<SelectPhotoResponse> => {
    const alert = useAlert();

    return new Promise<SelectPhotoResponse>((resolve, reject) => {
      if (showOptions?.hidePhoto && showOptions?.hideGallery) {
        return resolve(appendFileToFormData( showOptions.isPdfUploadType));
      }

      const buttons = [];
      if (!showOptions || !showOptions.hidePhoto) {
        buttons.push({
          text: t("alerts.selectTakePhoto"),
          handler: async () => {
            resolve(
              await takePhoto({
                allowPortraitOnly: showOptions?.allowPortraitOnly,
              })
            );
          },
        });
      }
      if (!showOptions || !showOptions.hideGallery) {
        buttons.push({
          text: t("alerts.selectUseGallery"),
          handler: async () => {
            resolve(
              await choosePhotoFromGallery(limit, {
                allowPortraitOnly: showOptions?.allowPortraitOnly,
              })
            );
          },
        });
      }
      if (!showOptions || !showOptions.hideFile) {
        buttons.push({
          text: "Datei wählen",
          handler: async () => {
            // @ts-ignore
            resolve(await appendFileToFormData( showOptions.isPdfUploadType ));
          },
        });
      }
      buttons.push({
        text: t("alerts.abort"),
        handler: async () => {
          resolve({ success: false, data: undefined });
        },
      });

      alert
        .showAdv("Bitte auswählen...", "", buttons, {
          backdropDismiss: false,
        })
        .then();
    });
  };

  const duplicatePhoto = (photo: APhoto): Promise<SelectPhotoResponse> => {
    if (isStrapiPhoto(photo)) {
      console.warn("STrapi photos not supported yet.");

      return getCompressedFiles(getPhotoUrl(photo)!, {
        allowPortraitOnly: true,
      });
      // return new Promise<SelectPhotoResponse>((resolve, reject) => {
      //     resolve({ success: false, data: undefined});
      // })
    } else {
      const cachedPhoto = photo as CachedPhoto;
      return getCompressedFiles(
        cachedPhoto.webPath!,
        { allowPortraitOnly: true },
        cachedPhoto.format!
      );
    }
  };

  return { takePhoto, choosePhotoFromGallery, openPhotoDialog, duplicatePhoto };
}

/**
 *
 * Without reduced file size this function would look like this:
 *
 * const takePhoto = async (): Promise<CachedPhoto> => {
        const cameraPhoto = await Camera.getPhoto({
            resultType: CameraResultType.Uri,
            source: CameraSource.Camera,
            quality: 100,
        });

        return {
            filepath: undefined,
            format: cameraPhoto.format,
            webPath: cameraPhoto.webPath,
            path: cameraPhoto.path,
        };
    };
 */
